import React, { useState, useEffect, useMemo } from "react";

const PUBLIC = process.env.PUBLIC_URL;

const WORK_INTRO =
  "Developed and contributed to 35+ mobile applications across government, Operations & Safety, smart-city parking, real estate, scientific research, and productivity domains. Several applications are live on the App Store, while others are enterprise and internal solutions; a few of them are listed below.";

const LINK_LABELS = {
  live: null,
  enterprise: "Enterprise",
  unavailable: "Store unavailable",
};

const PROJECTS = [
  {
    id: "umang",
    title: "UMANG",
    titleNote: "custom framework",
    category: "Government",
    description:
      "Reusable iOS framework for utility bill payments, recharges, and PayU integration within India's multi-service government platform.",
    details: [
      "Reusable iOS framework for iPhone and iPad across bill payments and recharges.",
      "Electricity, water, gas, broadband, telephone, mobile, and DTH services.",
      "PayU API integration for payment processing and transaction workflows.",
      "Built for easy integration, reusability, and maintainability within UMANG.",
    ],
    imageUrl: `${PUBLIC}/assets/images/umang.webp`,
    url: "https://apps.apple.com/in/app/umang/id1236448857",
    linkStatus: "live",
  },
  {
    id: "icarus",
    title: "ICARUS OPS",
    category: "Operations & Safety",
    description:
      "Checklist-based operations platform that reduces human error wherever procedures must be followed — aviation, data centers, warehouses, mining, and similar environments.",
    details: [
      "Checklist-driven workflows to reduce human error in the field.",
      "Used across aviation, data centers, warehouses, mining, and similar sites.",
      "Offline record sync with background processing.",
      "Sync performance improved from 30+ minutes to about 8–10 minutes.",
    ],
    imageUrl: `${PUBLIC}/assets/images/icarus.webp`,
    url: "https://apps.apple.com/in/app/icarus-ops/id1459253884",
    linkStatus: "live",
  },
  {
    id: "parklinq",
    title: "Parklinq",
    category: "Smart City Parking",
    description:
      "Enterprise iOS kiosk apps for parking validation, Stripe Tap to Pay, enforcement workflows, and JAMF MDM deployment.",
    details: [
      "Enterprise iOS kiosk apps for parking validation, payment, and enforcement.",
      "Stripe Tap to Pay and payment workflow integration.",
      "JAMF MDM deployment and device configuration.",
      "Multiple app variants within the same parking ecosystem.",
    ],
    imageUrl: `${PUBLIC}/assets/images/parklinq.webp`,
    url: null,
    linkStatus: "enterprise",
  },
  {
    id: "iqadha",
    title: "iQadha",
    category: "Productivity",
    description:
      "Helps Muslims calculate, track, and make up missed obligatory prayers and fasts.",
    details: [
      "Track missed prayers and fasts with qadha calculations.",
      "Reminders and progress views to help make up missed worship.",
    ],
    imageUrl: `${PUBLIC}/assets/images/iqadha.webp`,
    url: "https://apps.apple.com/us/app/iqadha/id1395338502",
    linkStatus: "live",
  },
  {
    id: "listerpros",
    title: "Listerpros",
    category: "Real Estate",
    description: "Real estate listing and scheduling application.",
    details: [
      "Property listing and discovery workflows for agents and prospective buyers.",
      "Showing and appointment scheduling with calendar-based coordination.",
      "Native iOS UI for listings, media, and client communication.",
    ],
    imageUrl: `${PUBLIC}/assets/images/listenPro.webp`,
    url: null,
    linkStatus: "unavailable",
  },
  {
    id: "petdialog",
    title: "PetDialog Studies App",
    category: "Research",
    description: "Pet data collection app supporting scientific research studies.",
    details: [
      "Structured pet health and behavior data collection for research studies.",
      "Guided study participant workflows with consistent, validated data entry.",
      "Secure submission of observations to support veterinary and scientific research.",
    ],
    imageUrl: `${PUBLIC}/assets/images/petDialog.webp`,
    url: null,
    linkStatus: "unavailable",
  },
  {
    id: "choueifat",
    title: "Choueifat",
    category: "Education",
    description: "School portal and communication app for the Choueifat community.",
    details: [
      "School portal for students, parents, and staff.",
      "Announcements, schedules, and community communication.",
      "Mobile access to school information and updates.",
    ],
    imageUrl: `${PUBLIC}/assets/images/choueifat.webp`,
    url: null,
    linkStatus: "unavailable",
  },
  {
    id: "vision-tt",
    title: "Vision Time Tracking",
    category: "Productivity",
    description: "Mobile time tracking for teams and field workflows.",
    imageUrl: `${PUBLIC}/assets/images/vtt.jpg`,
    url: null,
    linkStatus: "unavailable",
  },
  {
    id: "adrienne",
    title: "Adrienne's Awesome Energy",
    category: "Lifestyle",
    description: "Personal wellness and energy coaching experience.",
    imageUrl: `${PUBLIC}/assets/images/adrine.webp`,
    url: null,
    linkStatus: "unavailable",
  },
  {
    id: "tipl",
    title: "Tipl Rewards",
    category: "Rewards",
    description: "Customer rewards and loyalty mobile application.",
    imageUrl: `${PUBLIC}/assets/images/tiple.webp`,
    url: null,
    linkStatus: "unavailable",
  },
];

const chunkProjects = (projects, size) => {
  const rows = [];
  for (let index = 0; index < projects.length; index += size) {
    rows.push(projects.slice(index, index + size));
  }
  return rows;
};

const getArrowPosition = (index, columnsPerRow) =>
  ((index + 0.5) / columnsPerRow) * 100;

const Work = () => {
  const [visibleProjectsCount, setVisibleProjectsCount] = useState(4);
  const [columnsPerRow, setColumnsPerRow] = useState(2);
  const [activeProjectId, setActiveProjectId] = useState(null);

  useEffect(() => {
    const updateLayout = () => {
      const isDesktop = window.innerWidth >= 768;
      setVisibleProjectsCount(isDesktop ? 6 : 4);
      setColumnsPerRow(isDesktop ? 3 : 2);
    };

    window.addEventListener("resize", updateLayout);
    updateLayout();

    return () => window.removeEventListener("resize", updateLayout);
  }, []);

  const visibleProjects = useMemo(
    () => PROJECTS.slice(0, visibleProjectsCount),
    [visibleProjectsCount]
  );

  const projectRows = useMemo(
    () => chunkProjects(visibleProjects, columnsPerRow),
    [visibleProjects, columnsPerRow]
  );

  const handleLoadMoreClick = () => {
    setVisibleProjectsCount(PROJECTS.length);
  };

  const handleProjectActivate = (projectId) => {
    setActiveProjectId(projectId);
  };

  const handleProjectClick = (projectId) => {
    if (window.matchMedia("(hover: none)").matches) {
      setActiveProjectId((current) => (current === projectId ? null : projectId));
      return;
    }

    setActiveProjectId(projectId);
  };

  const handleRowLeave = () => {
    setActiveProjectId(null);
  };

  const renderTitleNote = (project) =>
    project.titleNote ? (
      <span className="project-title-note"> ({project.titleNote})</span>
    ) : null;

  const renderProjectTitle = (project, linked = false) => {
    const titleContent = (
      <>
        {project.title}
        {renderTitleNote(project)}
      </>
    );

    if (linked && project.linkStatus === "live" && project.url) {
      return (
        <a href={project.url} target="_blank" rel="noreferrer">
          {titleContent}
        </a>
      );
    }

    return titleContent;
  };

  const renderProjectAction = (project) =>
    project.linkStatus === "live" && project.url ? (
      <a
        href={project.url}
        className="btn btn-xs project-visit-btn"
        target="_blank"
        rel="noreferrer"
      >
        Visit
      </a>
    ) : (
      <span className={`project-link-badge project-link-badge--${project.linkStatus}`}>
        {LINK_LABELS[project.linkStatus]}
      </span>
    );

  const renderRowDetail = (project, arrowPosition) => (
    <div
      className="project-row-detail"
      style={{ "--arrow-position": `${arrowPosition}%` }}
    >
      <span className="project-row-detail-arrow" aria-hidden="true" />
      <div className="project-row-detail-inner">
        <div className="project-row-detail-media">
          <div
            className="project-row-detail-thumb"
            style={{ backgroundImage: `url('${project.imageUrl}')` }}
          />
          <span className="project-row-detail-category">{project.category}</span>
          <h4 className="project-row-detail-title">{renderProjectTitle(project)}</h4>
          <div className="project-row-detail-action">{renderProjectAction(project)}</div>
        </div>
        <div className="project-row-detail-content">
          <p className="project-row-detail-description">{project.description}</p>
          {project.details?.length > 0 && (
            <ul className="project-row-detail-list">
              {project.details.map((detail) => (
                <li key={detail}>{detail}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <section className="colorlib-work" data-section="work" id="work">
      <div className="colorlib-narrow-content">
        <div className="row">
          <div
            className="col-md-6 col-md-offset-3 col-md-pull-3 animate-box"
            data-animate-effect="fadeInLeft"
          >
            <span className="heading-meta">My Work</span>
            <h2 className="colorlib-heading animate-box">Recent Work</h2>
          </div>
        </div>
        <div
          className="row row-bottom-padded-sm animate-box"
          data-animate-effect="fadeInLeft"
        >
          <div className="col-md-12">
            <p className="work-intro">{WORK_INTRO}</p>
          </div>
        </div>
        <div className="row work-projects-row">
          <div className="col-md-12 animate-box">
            {projectRows.map((row, rowIndex) => {
              const activeIndex = row.findIndex((project) => project.id === activeProjectId);
              const activeProject = activeIndex >= 0 ? row[activeIndex] : null;

              return (
                <div
                  className="project-row-group"
                  key={`project-row-${rowIndex}`}
                  onMouseLeave={handleRowLeave}
                >
                  <div className="row custom-row work-projects-grid">
                    {row.map((project) => (
                      <div
                        className="custom-col-6 col-md-4 animate-box project-tile-wrap"
                        key={project.id}
                        data-animate-effect="fadeInLeft"
                      >
                        <button
                          type="button"
                          className={`project-tile-btn${
                            activeProjectId === project.id ? " is-active" : ""
                          }`}
                          onMouseEnter={() => handleProjectActivate(project.id)}
                          onFocus={() => handleProjectActivate(project.id)}
                          onClick={() => handleProjectClick(project.id)}
                          aria-expanded={activeProjectId === project.id}
                          aria-label={`Show details for ${project.title}`}
                        >
                          <span
                            className="project project-icon"
                            style={{ backgroundImage: `url('${project.imageUrl}')` }}
                          >
                            <span className="desc desc--compact">
                              <span className="con">
                                <span className="project-category">{project.category}</span>
                                <span className="project-title">
                                  {renderProjectTitle(project)}
                                </span>
                              </span>
                            </span>
                          </span>
                        </button>
                      </div>
                    ))}
                  </div>
                  {activeProject &&
                    renderRowDetail(
                      activeProject,
                      getArrowPosition(activeIndex, columnsPerRow)
                    )}
                </div>
              );
            })}
          </div>
        </div>
        {visibleProjectsCount < PROJECTS.length && (
          <div className="row">
            <div className="col-md-12 animate-box">
              <p>
                <button
                  onClick={handleLoadMoreClick}
                  className="btn btn-primary btn-lg btn-load-more"
                  type="button"
                >
                  Load more <i className="icon-reload" />
                </button>
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Work;
